// src/pages/PublicServicePortal.jsx
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { getDisplayName } from "../utils/displayName";
import VilkarKort from "../components/VilkarKort";
import { kundeFerdigLinje } from "../utils/ferdigDatoVisning";
import { useAngreFokus, useSideTittel } from "../components/tilgjengelighet";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
const UNDO_GRACE_MS = 3000; // 3 sekunder å angre

// Hvor mange meldinger som vises i "skjult" modus
const INITIAL_MESSAGES_SHOWN = 2;

// ——— Format-hjelpere ———
const fmtNOK = (n) =>
  new Intl.NumberFormat("no-NO", {
    style: "currency",
    currency: "NOK",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const fmtNOK2 = (n) =>
  new Intl.NumberFormat("no-NO", {
    style: "currency",
    currency: "NOK",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const fmtDT = (d) => {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt)
    ? dt
      .toLocaleString("no-NO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(",", "")
    : "";
};

const fmtDate = (d) => {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt)
    ? dt.toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
    : "";
};

export default function PublicServicePortal() {
  const { token } = useParams();

  // Butikkansatt som klikker lenken selv for å forhåndsvise — skal ikke telle
  // som at kunden faktisk har åpnet meldingen/pristilbudet
  const [isStaffPreview] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return q.get("staffPreview") === "1";
  });

  // Debugmodus via ?debug=1 eller localStorage.debugPortal === "1"
  const [debug] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return (
      q.get("debug") === "1" ||
      window.localStorage?.getItem("debugPortal") === "1"
    );
  });

  const backTo = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return q.get("back") || null;
  })[0];

  const [data, setData] = useState(null); // { service, customer? }
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // AbortController for å hindre at “gammel fetch” overskriver ny state
  const [controller, setController] = useState(null);

  // Inline-godkjenning
  const [inlineBusy, setInlineBusy] = useState(false);
  const [inlineMsg, setInlineMsg] = useState("");
  const [approveNoteInline, setApproveNoteInline] = useState("");

  // Vipps-betaling
  const [vippsBusy, setVippsBusy] = useState(false);
  const [vippsError, setVippsError] = useState("");
  const [vippsCheckingReturn, setVippsCheckingReturn] = useState(false);

  // Leveringsadresse (kun relevant når servicen har frakt)
  const [addrEditing, setAddrEditing] = useState(false);
  const [addrNavn, setAddrNavn] = useState("");
  const [addrGate, setAddrGate] = useState("");
  const [addrPostnr, setAddrPostnr] = useState("");
  const [addrPoststed, setAddrPoststed] = useState("");
  const [addrBusy, setAddrBusy] = useState(false);
  const [addrError, setAddrError] = useState("");

  // Meldinger (kunde ↔ verksted)
  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendError, setSendError] = useState("");

  // Kontroll for hvor mange meldinger som vises
  const [showAllMessages, setShowAllMessages] = useState(false);

  // Pending godkjenning/avslag (angrefrist)
  const [pendingDecision, setPendingDecision] = useState(null); // { type: "approve"|"decline", note: string }

  // Bildeopplasting
  const [imagePreviews, setImagePreviews] = useState([]); // [{ file, objectUrl }]
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageUploadError, setImageUploadError] = useState("");
  const imageInputRef = useRef(null);

  // --- SJEKKLISTE (oppsummering til portal) ---
  const [showChecklistDetails, setShowChecklistDetails] = useState(false);

  const svc = data?.service || null;

  useSideTittel(svc?.serviceRef ? `Service #${svc.serviceRef} – ${getDisplayName()}` : `Service – ${getDisplayName()}`);


  const chatLocked = useMemo(() => {
    const s = (svc?.status || "").toString().trim().toLowerCase();
    if (s === "kansellert") return true;
    if (s !== "avsluttet") return false;

    const avsluttetAt = svc?.statusAvsluttetAt ? new Date(svc.statusAvsluttetAt) : null;
    if (!avsluttetAt || Number.isNaN(avsluttetAt.getTime())) return true;

    const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - avsluttetAt.getTime() > ONE_WEEK_MS;
  }, [svc?.status, svc?.statusAvsluttetAt]);

  // ✅ Riktig base-path i henhold til backend-rutene dine:
  // GET  /workshop-services/public/:token
  // POST /workshop-services/public/:token/messages
  // (Approve finnes ikke i routen du limte inn — behold front-end, men håndter 404 pent)
  const PUBLIC_BASE = useMemo(
    () => `${API_BASE_URL}/workshop-services/public/${token}`,
    [token]
  );

  // Track portal open (avoid double-call in React StrictMode)
  const hasTrackedOpenRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    if (hasTrackedOpenRef.current) return; // ✅ prevents double call in dev StrictMode
    hasTrackedOpenRef.current = true;

    fetch(`${PUBLIC_BASE}/open`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source: isStaffPreview ? "staff" : "customer" }),
    })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.text().catch(() => "");
          if (debug) console.warn("[Portal] open-track failed:", r.status, body);
        } else if (debug) {
          const body = await r.json().catch(() => ({}));
          console.log("[Portal] open tracked:", body);
        }
      })
      .catch((e) => {
        if (debug) console.warn("[Portal] open-track error:", e);
      });
  }, [token, PUBLIC_BASE, debug, isStaffPreview]);

  // ——— Helper: GET (med cache-buster) ———
  async function fetchService({ signal } = {}) {
    const url = `${PUBLIC_BASE}?t=${Date.now()}`;
    if (debug) console.log("[Portal] GET", url);

    const r = await fetch(url, { signal, cache: "no-store" });

    if (debug) console.log("[Portal] GET status", r.status, r.statusText);
    if (!r.ok) { const b = await r.json().catch(() => ({})); const e = new Error(r.status === 410 && b.message ? b.message : "Fant ikke siden"); e.expired = r.status === 410; throw e; }

    const json = await r.json();
    if (debug) console.log("[Portal] GET payload", json);
    return json;
  }

  // ——— Første load ———
  useEffect(() => {
    if (!token) return;

    const c = new AbortController();
    setController(c);
    setLoading(true);
    setErr("");

    fetchService({ signal: c.signal })
      .then((json) => setData(json))
      .catch((e) => {
        if (e.name !== "AbortError") {
          console.error("[Portal] GET error:", e);
          setErr(e.expired ? e.message : "Lenken er ugyldig eller utløpt.");
        }
      })
      .finally(() => setLoading(false));

    return () => c.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ——— Retur fra Vipps: sjekk fersk status med én gang ———
  useEffect(() => {
    if (!token) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("vippsReturn") !== "1") return;

    setVippsCheckingReturn(true);
    fetch(`${PUBLIC_BASE}/vipps/status`, { cache: "no-store" })
      .then(() => fetchService())
      .then((json) => setData(json))
      .catch((e) => {
        if (debug) console.warn("[Portal] vipps status-sjekk feilet:", e);
      })
      .finally(() => setVippsCheckingReturn(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function startVippsPayment() {
    setVippsError("");
    setVippsBusy(true);
    try {
      const r = await fetch(`${PUBLIC_BASE}/vipps/create-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const json = await r.json().catch(() => ({}));

      if (!r.ok) {
        setVippsError(json?.message || "Kunne ikke starte Vipps-betaling.");
        return;
      }

      if (json?.redirectUrl) {
        window.location.href = json.redirectUrl;
      } else {
        setVippsError("Fikk ikke betalingslenke fra Vipps.");
      }
    } catch (e) {
      setVippsError("Kunne ikke starte Vipps-betaling.");
    } finally {
      setVippsBusy(false);
    }
  }

  const hasAddress = !!(
    svc?.sending?.adresse?.mottakerNavn &&
    svc?.sending?.adresse?.gate &&
    svc?.sending?.adresse?.postnr &&
    svc?.sending?.adresse?.poststed
  );

  useEffect(() => {
    if (!svc?.sending?.adresse) return;
    setAddrNavn(svc.sending.adresse.mottakerNavn || "");
    setAddrGate(svc.sending.adresse.gate || "");
    setAddrPostnr(svc.sending.adresse.postnr || "");
    setAddrPoststed(svc.sending.adresse.poststed || "");
  }, [svc?.sending?.adresse]);

  async function saveLeveringsadresse() {
    setAddrError("");
    if (!addrNavn.trim() || !addrGate.trim() || !addrPostnr.trim() || !addrPoststed.trim()) {
      setAddrError("Alle felt er påkrevd.");
      return;
    }
    setAddrBusy(true);
    try {
      const r = await fetch(`${PUBLIC_BASE}/leveringsadresse`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mottakerNavn: addrNavn.trim(),
          gate: addrGate.trim(),
          postnr: addrPostnr.trim(),
          poststed: addrPoststed.trim(),
        }),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) {
        setAddrError(json?.message || "Kunne ikke lagre adresse.");
        return;
      }
      setData((prev) => (prev ? { ...prev, service: { ...prev.service, sending: json.sending } } : prev));
      setAddrEditing(false);
    } catch (e) {
      setAddrError("Kunne ikke lagre adresse.");
    } finally {
      setAddrBusy(false);
    }
  }

  const checklistSummary = useMemo(() => {
    if (!svc) return [];

    const mapRow = (row) => {
      if (!row) return null;

      const slug =
        row.slug || row.punktSlug || row.key || row.id || row.title || undefined;

      const title = row.title || row.punktNavn || row.punktSlug || "Punkt";

      const deviationRaw = row.deviation ?? row.avvik ?? row.avvikValue ?? false;
      const doneRaw = row.done ?? row.doneValue ?? false;
      const okRaw = row.ok ?? row.okValue ?? false;

      const commentRaw = row.commentValue || row.comment || row.note || "";

      const deviation = !!deviationRaw;
      const done = !!doneRaw;
      const ok = !!okRaw && !deviation;
      const comment = (commentRaw || "").trim();

      const hasAny = ok || deviation || done || !!comment;
      if (!hasAny) return null;

      return { slug, title, deviation, done, ok, comment };
    };

    const rows = [];

    // 1) Ferdig summary direkte på service
    if (Array.isArray(svc.checklistSummary)) {
      svc.checklistSummary.forEach((row) => {
        if (row?.visible === false) return;
        const mapped = mapRow(row);
        if (mapped) rows.push(mapped);
      });
      return rows;
    }

    // 2) Rå sjekkliste på service
    let rawChecklist = null;

    if (Array.isArray(svc.checklist)) rawChecklist = svc.checklist;
    else if (svc.checklist && Array.isArray(svc.checklist.summary))
      rawChecklist = svc.checklist.summary;
    else if (svc.checklist && Array.isArray(svc.checklist.items))
      rawChecklist = svc.checklist.items;

    // 3) FALLBACK: sjekkliste fra customerSnapshot
    if (!rawChecklist && Array.isArray(svc.customerSnapshot?.checklist)) {
      rawChecklist = svc.customerSnapshot.checklist;
    }

    if (rawChecklist) {
      rawChecklist.forEach((row) => {
        if (row?.visible === false) return;
        const mapped = mapRow(row);
        if (mapped) rows.push(mapped);
      });
    }

    return rows;
  }, [svc]);

  const hasChecklist = useMemo(() => {
    if (checklistSummary.length > 0) return true;
    if (Array.isArray(svc?.checklist) && svc.checklist.length > 0) return true;
    if (Array.isArray(svc?.checklistSummary) && svc.checklistSummary.length > 0)
      return true;
    if (
      Array.isArray(svc?.customerSnapshot?.checklist) &&
      svc.customerSnapshot.checklist.length > 0
    )
      return true;
    return false;
  }, [checklistSummary.length, svc]);

  // Totalt antall synlige sjekkpunkter på servicen, uavhengig av om de har
  // fått en verdi ennå — brukes til å avgjøre om sjekklisten er fullført.
  const checklistVisibleTotal = useMemo(() => {
    if (!svc) return 0;
    const countVisible = (arr) => arr.filter((r) => r?.visible !== false).length;

    if (Array.isArray(svc.checklistSummary)) return countVisible(svc.checklistSummary);
    if (Array.isArray(svc.checklist)) return countVisible(svc.checklist);
    if (svc.checklist && Array.isArray(svc.checklist.summary))
      return countVisible(svc.checklist.summary);
    if (svc.checklist && Array.isArray(svc.checklist.items))
      return countVisible(svc.checklist.items);
    if (Array.isArray(svc?.customerSnapshot?.checklist))
      return countVisible(svc.customerSnapshot.checklist);
    return 0;
  }, [svc]);

  const checklistComplete =
    hasChecklist &&
    checklistVisibleTotal > 0 &&
    checklistSummary.length === checklistVisibleTotal;

  const checklistStats = useMemo(() => {
    const total = checklistSummary.length;
    const deviations = checklistSummary.filter((r) => r.deviation).length;
    const perform = checklistSummary.filter((r) => r.deviation && r.done).length;
    const notPerform = checklistSummary.filter(
      (r) => r.deviation && !r.done
    ).length;
    const okCount = checklistSummary.filter((r) => r.ok && !r.deviation).length;

    return { total, deviations, perform, notPerform, okCount };
  }, [checklistSummary]);

  // Ref til toppen av pristilbudet
  const quoteRef = useRef(null);

  // Ref til timer for angrefrist
  const pendingTimerRef = useRef(null);
  const { angreRef, startRef, vedtakRef } = useAngreFokus(
    !!pendingDecision,
    typeof svc?.quote?.approved?.ok === "boolean" && !!svc?.quote?.approved?.at
  );

  function scrollToQuote() {
    if (!quoteRef.current) return;
    quoteRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  useEffect(() => {
    if (!debug || !svc) return;
    console.log("[Portal] checklist debug", {
      svcChecklist: svc.checklist,
      svcChecklistSummary: svc.checklistSummary,
      snapshotChecklist: svc.customerSnapshot?.checklist,
      checklistSummaryMapped: checklistSummary,
    });
  }, [debug, svc, checklistSummary]);

  useEffect(() => {
    return () => {
      if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    };
  }, []);

  // Logg når vi får ny service inn
  useEffect(() => {
    if (!debug) return;
    console.groupCollapsed("[Portal] service state changed");
    console.log("svc:", svc);
    console.log("svc.quote:", svc?.quote);
    console.log("svc.messages:", svc?.messages);
    console.groupEnd();
  }, [debug, svc]);



  // Produkt-info (kompakt)
  const produkt = useMemo(
    () => ({
      merke: svc?.produkt?.merke || svc?.produkt?.produktmerke || "Produkt",
      type: svc?.produkt?.type || svc?.produkt?.produkttype || "",
      farge: svc?.produkt?.farge || "",
      butikk: svc?.produkt?.butikk || "",
      leveringsDato: svc?.produkt?.leveringsDato || null,
      forventetFerdigDato: svc?.produkt?.forventetFerdigDato || null,
      ferdigDatoValgfri: !!svc?.produkt?.ferdigDatoValgfri,
      onsketFerdigDato: svc?.produkt?.onsketFerdigDato || null,
      kommentar: svc?.produkt?.kommentar || "",
    }),
    [svc]
  );


  // Stabile array-referanser
  const serviceLines = useMemo(
    () => (Array.isArray(svc?.serviceLines) ? svc.serviceLines : []),
    [svc?.serviceLines]
  );
  const partsLines = useMemo(
    () => (Array.isArray(svc?.partsLines) ? svc.partsLines : []),
    [svc?.partsLines]
  );
  const fraktLines = useMemo(
    () => (Array.isArray(svc?.fraktLines) ? svc.fraktLines : []),
    [svc?.fraktLines]
  );

  const customerVisibleImages = useMemo(() => {
  const raw = Array.isArray(svc?.images) ? svc.images : [];
  return raw.filter((img) => img?.visibleToCustomer === true);
}, [svc?.images]);

  const customerVisibleDocuments = useMemo(() => {
    const raw = Array.isArray(svc?.documents) ? svc.documents : [];
    return raw.filter((doc) => doc?.visibleToCustomer === true);
  }, [svc?.documents]);

const buildImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_BASE_URL}${url}`;
};

  // Meldinger (sortert: nyeste øverst)
  const messages = useMemo(() => {
    const raw = Array.isArray(svc?.messages) ? svc.messages : [];
    return [...raw].sort((a, b) => {
      const da = a.createdAt || a.at || 0;
      const db = b.createdAt || b.at || 0;
      return new Date(db) - new Date(da);
    });
  }, [svc?.messages]);

  // Nyeste øverst: enten alle, eller de 2 nyeste
  const messagesToShow = useMemo(() => {
    if (!messages.length) return [];
    return showAllMessages ? messages : messages.slice(0, INITIAL_MESSAGES_SHOWN);
  }, [messages, showAllMessages]);

  // Summer
  const totals = useMemo(() => {
    const arbeid = serviceLines.reduce(
      (s, l) => (l.reklamasjon || l.gratisarbeid ? s : s + (Number(l.pris) || 0)),
      0
    );
    const deler = partsLines.reduce((s, l) => {
      if (l.isWarranty) return s;
      const line =
        (Number(l.price) || 0) * (Number(l.qty) || 1) - (Number(l.discount) || 0);
      return s + Math.max(0, line);
    }, 0);
    const frakt = fraktLines.reduce((s, l) => s + (Number(l.pris) || 0), 0);
    return { arbeid, deler, frakt, total: arbeid + deler + frakt };
  }, [serviceLines, partsLines, fraktLines]);

  // ——— Inline-aksept ———
  async function approveQuoteInline(noteFromCaller) {
    if (!token) return;
    setInlineBusy(true);
    setInlineMsg("");

    const note =
      typeof noteFromCaller === "string"
        ? noteFromCaller
        : (approveNoteInline || "").trim();

    const payload = { note };

    try {
      // ✅ Riktig base
      const url = `${PUBLIC_BASE}/approve`;
      if (debug) console.log("[Portal] POST", url, "payload:", payload);

      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const ct = resp.headers.get("content-type") || "";
      const isJson = ct.includes("application/json");
      const body = isJson
        ? await resp.json().catch(() => ({}))
        : await resp.text().catch(() => "");

      if (debug) {
        console.log("[Portal] POST status", resp.status, resp.statusText);
        console.log("[Portal] POST content-type", ct);
        console.log("[Portal] POST body", body);
      }

      if (!resp.ok) {
        // Typisk nå: 404 fordi approve-route ikke finnes i backend-koden du viste.
        throw new Error((body && body.message) || "Kunne ikke registrere godkjenning");
      }

      setData((prev) =>
        prev
          ? {
            ...prev,
            service: {
              ...prev.service,
              quote: {
                ...(prev.service?.quote || { enabled: true }),
                approved: {
                  ok: true,
                  at: body?.approved?.at || new Date().toISOString(),
                  note,
                },
              },
            },
          }
          : prev
      );

      controller?.abort?.();
      const c2 = new AbortController();
      setController(c2);
      const fresh = await fetchService({ signal: c2.signal });
      setData(fresh);

      setInlineMsg("Tilbudet er godkjent. Takk!");
    } catch (e) {
      console.error("[Portal] POST error:", e);
      setInlineMsg(
        e?.message?.includes("404")
          ? "Godkjenning er ikke aktivert i systemet ennå. Kontakt butikken."
          : "Beklager, noe gikk galt. Prøv igjen senere eller kontakt butikken."
      );
    } finally {
      setInlineBusy(false);
    }
  }

  async function declineQuoteInline(noteFromCaller) {
    if (!token) return;
    setInlineBusy(true);
    setInlineMsg("");

    const note =
      typeof noteFromCaller === "string"
        ? noteFromCaller
        : (approveNoteInline || "").trim();

    const payload = { note, decline: true };

    try {
      // ✅ Riktig base
      const url = `${PUBLIC_BASE}/approve`;
      if (debug) console.log("[Portal] POST (decline)", url, payload);

      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.message || "Kunne ikke avslå tilbudet");

      setData((prev) =>
        prev
          ? {
            ...prev,
            service: {
              ...prev.service,
              quote: {
                ...(prev.service?.quote || { enabled: true }),
                approved: {
                  ok: false,
                  at: new Date().toISOString(),
                  note,
                },
              },
            },
          }
          : prev
      );

      setInlineMsg("Tilbudet er avslått. Takk for tilbakemeldingen!");
    } catch (e) {
      console.error("[Portal] Decline error:", e);
      setInlineMsg(
        e?.message?.includes("404")
          ? "Avslå er ikke aktivert i systemet ennå. Kontakt butikken."
          : "Beklager, noe gikk galt. Prøv igjen senere."
      );
    } finally {
      setInlineBusy(false);
    }
  }

  function startInlineDecision(type) {
    if (!token) return;

    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }

    const note = (approveNoteInline || "").trim();
    setPendingDecision({ type, note });

    setInlineMsg(
      type === "approve"
        ? "Tilbudet er markert som godkjent. Endringen blir lagret om noen sekunder, men du kan angre før det."
        : "Tilbudet er markert som avslått. Endringen blir lagret om noen sekunder, men du kan angre før det."
    );

    pendingTimerRef.current = setTimeout(() => {
      if (type === "approve") approveQuoteInline(note);
      else declineQuoteInline(note);

      setPendingDecision(null);
      pendingTimerRef.current = null;
    }, UNDO_GRACE_MS);
  }

  function cancelPendingDecision() {
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
    setPendingDecision(null);
    setInlineMsg("Valget er angret. Ingen endring ble lagret.");
  }


  // ——— Send melding fra kunde ———
  async function sendMessage() {
    if (!token) return;

    if (chatLocked) {
      setSendError("Denne servicen er avsluttet. Dialogen er stengt.");
      return;
    }

    const text = messageText.trim();
    if (!text) return;

    setSendingMessage(true);
    setSendError("");

    const payload = {
      from: "customer",
      text,
      fromName: svc?.serviceRef ? `Ref #${svc.serviceRef}` : "Kunde",
    };

    try {
      // ✅ Riktig base
      const url = `${PUBLIC_BASE}/messages`;
      if (debug) console.log("[Portal] POST message", url, payload);

      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const body = await resp.json().catch(() => ({}));
      if (debug) {
        console.log("[Portal] POST message status", resp.status, resp.statusText);
        console.log("[Portal] POST message body", body);
      }

      if (!resp.ok) {
        throw new Error(body?.message || "Kunne ikke sende meldingen. Prøv igjen.");
      }

      const newMessages = Array.isArray(body.messages) ? body.messages : svc?.messages;

      setData((prev) =>
        prev
          ? {
            ...prev,
            service: { ...prev.service, messages: newMessages },
          }
          : prev
      );

      setMessageText("");
    } catch (e) {
      console.error("[Portal] sendMessage error:", e);
      setSendError(e?.message || "Kunne ikke sende meldingen. Prøv igjen senere.");
    } finally {
      setSendingMessage(false);
    }
  }

  const handleImageSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const previews = files.map((file) => ({
      file,
      objectUrl: URL.createObjectURL(file),
    }));
    setImagePreviews((prev) => [...prev, ...previews]);
    setImageUploadError("");
    e.target.value = "";
  }, []);

  const removeImagePreview = useCallback((idx) => {
    setImagePreviews((prev) => {
      URL.revokeObjectURL(prev[idx].objectUrl);
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  const uploadImages = useCallback(async () => {
    if (!imagePreviews.length || uploadingImages) return;
    setUploadingImages(true);
    setImageUploadError("");
    try {
      const form = new FormData();
      imagePreviews.forEach(({ file }) => form.append("images", file));

      const resp = await fetch(`${PUBLIC_BASE}/images`, { method: "POST", body: form });
      const body = await resp.json().catch(() => ({}));

      if (!resp.ok) throw new Error(body?.message || "Opplasting feilet");

      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          service: {
            ...prev.service,
            images: Array.isArray(body.images) ? body.images : (prev.service?.images || []),
            messages: Array.isArray(body.messages) ? body.messages : (prev.service?.messages || []),
          },
        };
      });

      imagePreviews.forEach(({ objectUrl }) => URL.revokeObjectURL(objectUrl));
      setImagePreviews([]);
    } catch (e) {
      setImageUploadError(e?.message || "Kunne ikke laste opp bilder. Prøv igjen.");
    } finally {
      setUploadingImages(false);
    }
  }, [imagePreviews, uploadingImages, PUBLIC_BASE]);

  // ——— Render ———
  if (loading) return <div className="p-4" role="status">Laster…</div>;
  if (err || !svc)
    return (
      <div className="p-4">
        <h1 className="text-xl font-semibold mb-1">Serviceinformasjon</h1>
        <p className={err ? "text-red-700" : ""} role={err ? "alert" : undefined}>{err || "Fant ikke service."}</p>
      </div>
    );

  const quoteEnabled = (svc?.quote?.enabled ?? true) !== false;
  const approval = svc?.quote?.approved || null;
  // Et vedtak har alltid tidspunkt; modellen har ok: false som standard også før kunden har svart
  const hasDecision = typeof approval?.ok === "boolean" && !!approval?.at;
  const isApproved = approval?.ok === true;

  const showKvittering =
    (svc?.status || "").toLowerCase() === "avsluttet" &&
    (serviceLines.length > 0 || partsLines.length > 0);

  // Sticky-boksen vises så lenge pristilbudet er aktivt og kvittering ikke vises
  const inlineApprovalVisible = quoteEnabled && !showKvittering;

  // Kommentar knyttet til pristilbudet (fra verksted)
  const quoteComment =
    (svc?.quoteDescription && svc.quoteDescription.trim()) ||
    svc?.quote?.comment ||
    svc?.quote?.commentText ||
    svc?.quote?.description ||
    "";

  // Sjekkliste vist som en del av betalingsinformasjonen (både aktivt
  // pristilbud og avsluttet/kvittering) — kun når alle punkter er fylt ut.
  const checklistSection = checklistComplete ? (
    <div className="border rounded-lg p-3 md:p-4 mt-4 bg-slate-50">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold text-sm md:text-base">Tilstandsrapport</h3>

        <button
          type="button"
          onClick={() => setShowChecklistDetails((v) => !v)}
          aria-expanded={showChecklistDetails}
          aria-controls="tilstandsrapport-detaljer"
          className="text-xs md:text-sm px-3 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
        >
          {showChecklistDetails ? "Skjul detaljer" : "Vis detaljer"}
        </button>
      </div>

      <div className="mt-2 text-xs md:text-sm text-slate-700 space-y-1">
        {checklistStats.deviations === 0 ? (
          <p>Ingen avvik registrert i sjekklisten.</p>
        ) : (
          <>
            <p>
              Vi har gått gjennom en sjekkliste på{" "}
              <span className="font-semibold">{checklistStats.total}</span>{" "}
              punkt.
            </p>
            <p>
              <span className="font-semibold">{checklistStats.deviations}</span>{" "}
              punkt har avvik, hvor{" "}
              <span className="font-semibold">{checklistStats.perform}</span>{" "}
              er foreslått utført i dette tilbudet og{" "}
              <span className="font-semibold">{checklistStats.notPerform}</span>{" "}
              er registrert som avvik som{" "}
              <span className="font-semibold">ikke</span> utføres nå.
            </p>
          </>
        )}
      </div>

      {showChecklistDetails && (
        <ul id="tilstandsrapport-detaljer" className="mt-3 space-y-2 text-xs md:text-sm">
          {checklistSummary.map((row) => (
            <li
              key={row.slug || row.title}
              className="border rounded-md px-2 py-2 flex flex-col gap-1 bg-white"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">{row.title}</span>

                <div className="flex flex-wrap gap-1">
                  {row.ok && !row.deviation && (
                    <span className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                      OK
                    </span>
                  )}

                  {row.done && (
                    <span className="px-2 py-0.5 rounded-full text-[11px] bg-blue-50 text-blue-700 border border-blue-200">
                      Utføres
                    </span>
                  )}

                  {row.deviation && (
                    <span className="px-2 py-0.5 rounded-full text-[11px] bg-red-50 text-red-700 border border-red-200">
                      {row.done ? "Avvik utbedres" : "Avvik utbedres ikke"}
                    </span>
                  )}
                </div>
              </div>

              {row.comment && (
                <p className="text-slate-700 whitespace-pre-wrap break-words">
                  {row.comment}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {!showChecklistDetails && (
        <p className="mt-2 text-[11px] text-slate-500">
          Trykk på <span className="underline">Vis detaljer</span> for å se
          alle sjekkpunktene.
        </p>
      )}
    </div>
  ) : null;

  return (
    <div>
      {backTo === 'min-side' && (
        <div className="bg-white border-b-4 border-red-600 px-4 py-3 mb-2">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <img
              src="/images/RoykenSykkelservice_logo_transparent.png"
              alt={getDisplayName()}
              className="h-12 w-auto object-contain"
            />
            <a
              href="/min-side"
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Din side
            </a>
          </div>
        </div>
      )}
    <div className={`mx-auto w-full p-4 md:p-6 ${backTo === 'min-side' ? 'max-w-lg' : 'max-w-3xl'}`}>
      <h1 className="text-xl md:text-2xl font-semibold mb-1">
        Serviceinformasjon
      </h1>
      <p className="text-gray-600 mb-4 md:mb-6 text-sm">
        Ref.{" "}
        {svc.serviceRef ? (
          <span className="font-mono">#{svc.serviceRef}</span>
        ) : (
          "—"
        )}{" "}
        · Opprettet {fmtDT(svc.createdAt)}
      </p>

      <VilkarKort
        vilkar={data?.vilkar}
      />

      {/* Kontaktinfo endres på Din side, ikke via lenken (kundeflyt fase 3) */}
      <div className="border rounded-lg p-3 md:p-4 mb-4 text-sm">
        <p className="text-gray-800">
          Vil du endre e-post eller mobilnummer? Det gjør du på{" "}
          <a href="/min-side" className="text-blue-700 underline font-medium">Din side</a>.
        </p>
      </div>

      {/* Sticky godkjenning øverst */}
      {inlineApprovalVisible && (
        <div
          className="sticky top-0 z-30 border border-emerald-200 bg-emerald-50/90 backdrop-blur supports-[backdrop-filter]:bg-emerald-50/70 rounded-md p-2 md:p-3 mb-4 shadow"
          role="region"
          aria-label="Godkjenn eller avslå tilbud"
        >
          {!hasDecision ? (
            <>
              <div className="flex flex-col md:flex-row md:items-center gap-2">
                <input
                  id="approveNoteInline"
                  aria-label="Kommentar til tilbudet (valgfritt)"
                  type="text"
                  className="flex-grow border rounded px-3 py-2 text-sm"
                  placeholder='Kort kommentar (valgfritt), f.eks. "Utfør service" eller "Ikke utfør"'
                  value={approveNoteInline}
                  onChange={(e) => setApproveNoteInline(e.target.value)}
                />

                <div className="flex gap-2 shrink-0">
                  <button
                    ref={startRef}
                    type="button"
                    onClick={() => startInlineDecision("approve")}
                    disabled={inlineBusy || !!pendingDecision}
                    className={`px-4 py-2 rounded text-white text-sm font-medium ${inlineBusy || pendingDecision
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-emerald-700 hover:bg-emerald-800"
                      }`}
                  >
                    {inlineBusy ? "Sender…" : "Godkjenn"}
                  </button>

                  <button
                    type="button"
                    onClick={() => startInlineDecision("decline")}
                    disabled={inlineBusy || !!pendingDecision}
                    className={`px-4 py-2 rounded text-white text-sm font-medium ${inlineBusy || pendingDecision
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-700"
                      }`}
                  >
                    {inlineBusy ? "Sender…" : "Avslå"}
                  </button>

                  <button
                    type="button"
                    onClick={scrollToQuote}
                    className="px-4 py-2 rounded border border-emerald-600 text-emerald-700 text-sm font-medium bg-white hover:bg-emerald-50"
                  >
                    Se tilbud
                  </button>
                </div>
              </div>

              <div className={inlineMsg ? "mt-2 text-sm text-emerald-900" : ""} role="status">
                {inlineMsg && (<>
                  {inlineMsg}{" "}
                  {pendingDecision && (
                    <button
                      ref={angreRef}
                      type="button"
                      onClick={cancelPendingDecision}
                      className="underline font-medium ml-1"
                    >
                      Angre
                    </button>
                  )}
                </>)}
              </div>
            </>
          ) : (
            <div
              ref={vedtakRef}
              tabIndex={-1}
              className={`text-sm md:text-base ${isApproved ? "text-emerald-700" : "text-red-700"
                }`}
            >
              {isApproved
                ? "Tilbudet er godkjent. Takk!"
                : "Tilbudet er avslått. Takk for tilbakemeldingen!"}

              {approval?.note && (
                <div className="text-gray-700 mt-1">Kommentar: {approval.note}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Produkt */}
      <div className="border rounded-lg p-3 md:p-4 mb-4">
        <h2 className="font-semibold mb-2 text-base md:text-lg">Produkt</h2>
        {produkt.merke ||
          produkt.type ||
          produkt.farge ||
          produkt.butikk ||
          produkt.leveringsDato ||
          produkt.forventetFerdigDato ||
          produkt.kommentar ? (
          <div className="space-y-1 text-sm">
            <div className="font-medium text-base">
              {produkt.merke}
              {produkt.type ? ` – ${produkt.type}` : ""}
              {produkt.farge ? ` (${produkt.farge})` : ""}
            </div>
            {produkt.butikk && (
              <div className="text-gray-600">Butikk: {produkt.butikk}</div>
            )}
            {produkt.leveringsDato && (
              <div className="text-gray-600">
                Leveringsdato: {fmtDate(produkt.leveringsDato)}
              </div>
            )}
            {(() => {
              const ferdig = kundeFerdigLinje(produkt, fmtDate, "Forventet ferdig innen");
              return ferdig ? (
                <div className="text-gray-600">
                  {ferdig.label}: {ferdig.tekst}
                </div>
              ) : null;
            })()}
          </div>
        ) : (
          <div className="text-gray-600 text-sm">Ingen produktdata registrert.</div>
        )}
        <div className="mt-3 text-sm">
          Status: <strong>{svc.status || "Ukjent"}</strong>
        </div>
      </div>

      {/* Kvittering – vises når servicen er avsluttet og det finnes tjenester/deler.
          Blir en fullverdig kvittering med bilagsnummer/mva/selger når Vipps-
          betalingen er gjennomført, ellers bare betalingsinformasjon. */}
      {showKvittering && (() => {
        const erBetaltKvittering = !!(svc?.salgsbilag?.nummer && svc?.vipps?.payment?.status === "betalt");
        const bilag = svc?.salgsbilag;

        return (
        <div className="border-2 border-emerald-300 rounded-lg p-3 md:p-4 mb-4 bg-emerald-50">
          <div className="flex items-center gap-2 mb-1">
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-600 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <h2 className="font-semibold text-base md:text-lg text-emerald-900">
              {erBetaltKvittering ? `Kvittering ${bilag.nummer}` : "Betalingsinformasjon"}
            </h2>
          </div>
          <p className="text-sm text-emerald-700 mb-3">
            {erBetaltKvittering
              ? "Kvittering for utført arbeid og gjennomført betaling."
              : "Oversikt over utført arbeid. Totalbeløpet er klart for betaling ved henting."}
          </p>

          {svc?.arbeidsbeskrivelse?.trim() && (
            <div className="mb-4 p-3 bg-white rounded border border-emerald-200">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Arbeidsbeskrivelse</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{svc.arbeidsbeskrivelse.trim()}</p>
            </div>
          )}

          {serviceLines.length > 0 && (
            <>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Arbeid</h3>
              <ul className="divide-y divide-emerald-100 mb-2">
                {serviceLines.map((l, i) => (
                  <li key={l._id || i} className="py-2 flex items-start justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{l.navn || "Tjeneste"}</div>
                      {l.kommentar && <div className="text-gray-600 text-xs">{l.kommentar}</div>}
                    </div>
                    <div className="shrink-0 text-right">
                      {l.reklamasjon ? (
                        <>
                          <div className="font-medium text-gray-600 line-through whitespace-nowrap"><span className="sr-only">Ordinær pris </span>{fmtNOK(l.pris)}</div>
                          <span className="inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Reklamasjon</span>
                        </>
                      ) : l.gratisarbeid ? (
                        <>
                          <div className="font-medium text-gray-600 line-through whitespace-nowrap"><span className="sr-only">Ordinær pris </span>{fmtNOK(l.pris)}</div>
                          <span className="inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">Gratisarbeid</span>
                        </>
                      ) : (
                        <div className="font-medium whitespace-nowrap">{fmtNOK(l.pris)}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Sum arbeid</span>
                <span className="font-medium">{fmtNOK(totals.arbeid)}</span>
              </div>
            </>
          )}

          {partsLines.length > 0 && (
            <>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-3 mb-1">Deler</h3>
              <ul className="divide-y divide-emerald-100 mb-2">
                {partsLines.map((p, i) => {
                  const price = Number(p.price) || 0;
                  const qty = Math.max(1, Number(p.qty) || 1);
                  const discountKr = Math.max(0, Number(p.discount) || 0);
                  const line = Math.max(0, price * qty - discountKr);
                  return (
                    <li key={`${i}-${p.product || "part"}`} className="py-2 flex items-start justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <div className="font-medium">{p.product || "Del"}{p.brand ? ` (${p.brand})` : ""}</div>
                        <div className="text-xs text-gray-600">{qty} stk · {fmtNOK(price)}</div>
                      </div>
                      <div className="shrink-0 text-right">
                        {p.isWarranty ? (
                          <>
                            <div className="font-medium text-gray-600 line-through whitespace-nowrap"><span className="sr-only">Ordinær pris </span>{fmtNOK(line)}</div>
                            <span className="inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Reklamasjon</span>
                          </>
                        ) : (
                          <div className="font-medium whitespace-nowrap">{fmtNOK(line)}</div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Sum deler</span>
                <span className="font-medium">{fmtNOK(totals.deler)}</span>
              </div>
            </>
          )}

          {fraktLines.length > 0 && (
            <>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-3 mb-1">Frakt</h3>
              <ul className="divide-y divide-emerald-100 mb-2">
                {fraktLines.map((f, i) => (
                  <li key={i} className="py-2 flex items-start justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <div className="font-medium">{f.beskrivelse || "Frakt"}</div>
                      {f.kommentar && <div className="text-gray-600 text-xs">{f.kommentar}</div>}
                    </div>
                    <div className="shrink-0 whitespace-nowrap font-medium">{fmtNOK(f.pris)}</div>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Sum frakt</span>
                <span className="font-medium">{fmtNOK(totals.frakt)}</span>
              </div>

              {/* Leveringsadresse — påkrevd før betaling når servicen har frakt */}
              <div className="mb-3 p-3 bg-white rounded border border-emerald-200">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Leveringsadresse</p>

                {hasAddress && !addrEditing ? (
                  <div className="text-sm">
                    <div>{svc.sending.adresse.mottakerNavn}</div>
                    <div>{svc.sending.adresse.gate}</div>
                    <div>{svc.sending.adresse.postnr} {svc.sending.adresse.poststed}</div>
                    {!erBetaltKvittering && (
                      <button
                        type="button"
                        onClick={() => setAddrEditing(true)}
                        className="mt-2 text-xs text-emerald-700 underline"
                      >
                        Endre adresse
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-600">Fyll inn adressen vi skal sende til før du betaler.</p>
                    <label className="block text-xs font-medium text-gray-700">
                      Mottakers navn
                      <input
                        type="text"
                        autoComplete="name"
                        value={addrNavn}
                        onChange={(e) => setAddrNavn(e.target.value)}
                        className="mt-0.5 w-full border rounded px-2 py-1.5 text-sm font-normal"
                      />
                    </label>
                    <label className="block text-xs font-medium text-gray-700">
                      Gateadresse
                      <input
                        type="text"
                        autoComplete="street-address"
                        value={addrGate}
                        onChange={(e) => setAddrGate(e.target.value)}
                        className="mt-0.5 w-full border rounded px-2 py-1.5 text-sm font-normal"
                      />
                    </label>
                    <div className="flex gap-2">
                      <label className="block w-24 text-xs font-medium text-gray-700">
                        Postnr.
                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="postal-code"
                          value={addrPostnr}
                          onChange={(e) => setAddrPostnr(e.target.value)}
                          className="mt-0.5 w-full border rounded px-2 py-1.5 text-sm font-normal"
                        />
                      </label>
                      <label className="block flex-1 text-xs font-medium text-gray-700">
                        Poststed
                        <input
                          type="text"
                          autoComplete="address-level2"
                          value={addrPoststed}
                          onChange={(e) => setAddrPoststed(e.target.value)}
                          className="mt-0.5 w-full border rounded px-2 py-1.5 text-sm font-normal"
                        />
                      </label>
                    </div>
                    {addrError && <p className="text-xs text-red-700" role="alert">{addrError}</p>}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={saveLeveringsadresse}
                        disabled={addrBusy}
                        className="px-3 py-1.5 rounded bg-emerald-700 text-white text-sm hover:bg-emerald-800 disabled:opacity-60"
                      >
                        {addrBusy ? "Lagrer…" : "Lagre adresse"}
                      </button>
                      {hasAddress && (
                        <button
                          type="button"
                          onClick={() => setAddrEditing(false)}
                          className="px-3 py-1.5 rounded border text-sm hover:bg-gray-50"
                        >
                          Avbryt
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {erBetaltKvittering ? (
            <div className="border-t border-emerald-300 mt-2 pt-3 text-sm space-y-1">
              <div className="flex justify-between text-emerald-800">
                <span>Sum eks. mva</span>
                <span>{fmtNOK2(bilag.sumEksMva)}</span>
              </div>
              <div className="flex justify-between text-emerald-800">
                <span>Mva {bilag.mvaSats ?? 25} %</span>
                <span>{fmtNOK2(bilag.mvaBelop)}</span>
              </div>
              <div className="flex justify-between items-baseline font-bold text-base text-emerald-900 pt-1">
                <span>TOTALT</span>
                <span>{fmtNOK2(bilag.totalInkMva)}</span>
              </div>
            </div>
          ) : (
            <div className="border-t border-emerald-300 mt-2 pt-3 flex justify-between items-baseline">
              <span className="font-semibold text-base text-emerald-900">Totalt å betale</span>
              <span className="font-bold text-xl text-emerald-900">{fmtNOK(totals.total)}</span>
            </div>
          )}

          {!!svc?.vipps?.requested && (
            <div className="mt-3">
              {svc?.vipps?.payment?.status === "betalt" ? (
                <div className="w-full px-4 py-3 rounded bg-emerald-100 border border-emerald-300 text-emerald-900 text-sm font-semibold text-center">
                  <span aria-hidden="true">✅ </span>Betalt med Vipps
                </div>
              ) : (
                <>
                  {/* Vipps-oransje mot hvit tekst er 3,1:1 — derfor stor, fet tekst (krav 3:1, U3) */}
                  <button
                    type="button"
                    onClick={startVippsPayment}
                    disabled={vippsBusy || (fraktLines.length > 0 && !hasAddress)}
                    className="w-full px-4 py-3 rounded text-white text-xl font-bold bg-[#FF5B24] hover:bg-[#e6501f] disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {vippsBusy ? "Starter Vipps…" : "Betal med Vipps"}
                  </button>
                  {fraktLines.length > 0 && !hasAddress && (
                    <p className="text-xs text-orange-800 mt-2 text-center">
                      Fyll inn leveringsadresse over før du kan betale.
                    </p>
                  )}
                  {vippsCheckingReturn && (
                    <p className="text-xs text-gray-600 mt-2 text-center" role="status">Sjekker betalingsstatus…</p>
                  )}
                  {["kansellert", "utlopt", "avbrutt"].includes(svc?.vipps?.payment?.status) && (
                    <p className="text-xs text-orange-800 mt-2 text-center" role="status">
                      Betalingen ble ikke fullført. Prøv igjen, eller betal ved henting.
                    </p>
                  )}
                  {vippsError && (
                    <p className="text-xs text-red-700 mt-2 text-center" role="alert">{vippsError}</p>
                  )}
                </>
              )}
            </div>
          )}

          {erBetaltKvittering && (
            <div className="border-t border-emerald-200 mt-3 pt-3 text-xs text-emerald-700">
              <div>{bilag.selger?.navn || "Røyken Sykkelservice"}</div>
              {bilag.selger?.orgnr && (
                <div>Org.nr. {bilag.selger.orgnr}{bilag.selger.mvaRegistrert ? " MVA" : ""}</div>
              )}
            </div>
          )}

          {checklistSection}
        </div>
        );
      })()}

      {/* Bilder godkjent for kunde */}
{customerVisibleImages.length > 0 && (
  <div className="border rounded-lg p-3 md:p-4 mb-4">
    <div className="flex items-center justify-between gap-2 mb-3">
      <h2 className="font-semibold text-base md:text-lg">Bilder</h2>
      <span className="text-xs text-gray-500">
        {customerVisibleImages.length} bilde
        {customerVisibleImages.length !== 1 ? "r" : ""}
      </span>
    </div>

<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
  {customerVisibleImages.map((img) => (
    <div
      key={img._id || img.filename}
      className="border rounded-lg overflow-hidden bg-gray-50 max-w-[140px]"
    >
      <a
        href={buildImageUrl(img.url)}
        target="_blank"
        rel="noreferrer"
        className="block relative"
      >
        <img
          src={buildImageUrl(img.url)}
          alt={img.caption || img.originalName || "Bilde"}
          className="w-full h-24 object-cover"
        />
        {img.uploadedByCustomer && (
          <span className="absolute top-1 left-1 text-[10px] bg-emerald-700 text-white px-1.5 py-0.5 rounded">
            Deg
          </span>
        )}
      </a>

          <div className="p-2">
            {(img.caption || img.originalName || img.filename) && (
              <div className="text-xs text-gray-800 break-words">
                {img.caption || img.originalName || img.filename}
              </div>
            )}

            {img.uploadedAt && (
              <div className="text-[11px] text-gray-500 mt-1">
                {fmtDT(img.uploadedAt)}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
)}

      {/* Dokumenter godkjent for kunde */}
      {customerVisibleDocuments.length > 0 && (
        <div className="border rounded-lg p-3 md:p-4 mb-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h2 className="font-semibold text-base md:text-lg">Dokumenter</h2>
            <span className="text-xs text-gray-500">
              {customerVisibleDocuments.length} dokument
              {customerVisibleDocuments.length !== 1 ? "er" : ""}
            </span>
          </div>

          <div className="space-y-2">
            {customerVisibleDocuments.map((doc) => (
              <a
                key={doc._id || doc.filename}
                href={buildImageUrl(doc.url)}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 border rounded-lg p-2.5 bg-gray-50 hover:bg-gray-100"
              >
                <span className="text-xl leading-none" aria-hidden="true">📄</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-blue-700 break-words">
                    {doc.caption || doc.originalName || doc.filename}
                  </div>
                  {doc.uploadedAt && (
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {fmtDT(doc.uploadedAt)}
                    </div>
                  )}
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Meldinger */}
      <div className="border rounded-lg p-3 md:p-4 mb-4">
        <h2 className="font-semibold mb-2 text-base md:text-lg">
          Dialog med verksted
        </h2>

        <div className="mb-3 space-y-2 text-sm">
          {messages.length === 0 ? (
            <p className="text-gray-600">
              Ingen meldinger ennå. Du kan starte dialogen med verkstedet under.
            </p>
          ) : (
            <>
              <ul className="space-y-2">
                {messagesToShow.map((m) => {
                  const fromCustomer = m.from === "customer";
                  const label = fromCustomer ? "Deg" : m.fromName || "Mekaniker";
                  const at = fmtDT(m.createdAt || m.at);

                  return (
                    <li
                      key={m._id || `${m.from}-${m.createdAt}-${m.text.slice(0, 10)}`}
                      className={`flex ${fromCustomer ? "justify-end" : "justify-start"}`}
                    >
                      <div className="max-w-[80%]">
                        <div
                          className={`text-[11px] mb-0.5 ${fromCustomer ? "text-right text-gray-500" : "text-gray-500"
                            }`}
                        >
                          {label}
                          {at ? ` · ${at}` : ""}
                        </div>
                        <div
                          className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${fromCustomer
                            ? "bg-emerald-700 text-white"
                            : "bg-gray-100 text-gray-900"
                            }`}
                        >
                          {m.text}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {messages.length > 2 && (
                <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
                  <span>
                    Viser {showAllMessages ? messages.length : INITIAL_MESSAGES_SHOWN} av{" "}
                    {messages.length} meldinger
                  </span>

                  <button
                    type="button"
                    onClick={() => setShowAllMessages((v) => !v)}
                    aria-expanded={showAllMessages}
                    className="underline text-emerald-700"
                  >
                    {showAllMessages ? "Skjul eldre meldinger" : "Vis alle meldinger"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="portalMessage" className="block text-xs font-medium text-gray-700">
            Din melding til mekaniker
          </label>
          <textarea
            id="portalMessage"
            disabled={chatLocked}
            className={`w-full border rounded px-3 py-2 text-sm min-h-[70px] ${chatLocked ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""
              }`}
            placeholder={
              chatLocked
                ? "Dialogen er stengt fordi servicen er avsluttet."
                : "Skriv en beskjed til verkstedet..."
            }
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
          />
          {sendError && <div className="text-xs text-red-700" role="alert">{sendError}</div>}

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={sendMessage}
              disabled={chatLocked || sendingMessage || !messageText.trim()}
              className={`px-4 py-2 rounded text-sm font-medium text-white ${chatLocked || sendingMessage || !messageText.trim()
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-emerald-700 hover:bg-emerald-800"
                }`}
            >
              {chatLocked
                ? "Dialog stengt"
                : sendingMessage
                  ? "Sender…"
                  : "Send melding"}
            </button>
            {!chatLocked && (
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                title="Last opp bilde"
                aria-label="Last opp bilde"
                className="ml-2 p-2 rounded border border-gray-300 hover:bg-gray-50 text-gray-600"
              >
                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            )}
            <p className="text-[11px] ml-2 text-gray-500 flex-1">
              Meldingen lagres sammen med servicejobben.
            </p>
          </div>

          {/* Skjult filinput */}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleImageSelect}
          />

          {/* Bildeforhåndsvisning */}
          {imagePreviews.length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="flex flex-wrap gap-2">
                {imagePreviews.map(({ objectUrl }, idx) => (
                  <div key={idx} className="relative w-20 h-20 rounded overflow-hidden border bg-gray-50">
                    <img src={objectUrl} alt={`Valgt bilde ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImagePreview(idx)}
                      aria-label={`Fjern bilde ${idx + 1}`}
                      className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs leading-none"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              {imageUploadError && (
                <div className="text-xs text-red-700" role="alert">{imageUploadError}</div>
              )}
              <button
                type="button"
                onClick={uploadImages}
                disabled={uploadingImages}
                className={`px-4 py-2 rounded text-sm font-medium text-white ${uploadingImages ? "bg-gray-400 cursor-not-allowed" : "bg-emerald-700 hover:bg-emerald-800"}`}
              >
                {uploadingImages ? "Laster opp…" : `Send ${imagePreviews.length} bilde${imagePreviews.length === 1 ? "" : "r"}`}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pristilbud */}
      {quoteEnabled && !showKvittering && (
        <>
          <div ref={quoteRef} className="border rounded-lg p-3 md:p-4 mb-4">
            <h2 className="font-semibold mb-2 text-base md:text-lg">Foreslått arbeid</h2>
            {serviceLines.length ? (
              <ul className="divide-y">
                {serviceLines.map((l, i) => (
                  <li
                    key={l._id || i}
                    className="py-2 flex items-start justify-between gap-3 text-sm"
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">{l.navn || "Tjeneste"}</div>
                      {l.kommentar ? <div className="text-gray-600">{l.kommentar}</div> : null}
                    </div>
                    <div className="shrink-0 text-right">
                      {l.reklamasjon ? (
                        <>
                          <div className="text-gray-600 line-through whitespace-nowrap"><span className="sr-only">Ordinær pris </span>{fmtNOK(l.pris)}</div>
                          <span className="inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Reklamasjon</span>
                        </>
                      ) : l.gratisarbeid ? (
                        <>
                          <div className="text-gray-600 line-through whitespace-nowrap"><span className="sr-only">Ordinær pris </span>{fmtNOK(l.pris)}</div>
                          <span className="inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">Gratisarbeid</span>
                        </>
                      ) : (
                        <div className="whitespace-nowrap">{fmtNOK(l.pris)}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-gray-600 text-sm">Ingen tjenester.</div>
            )}
            <div className="mt-3 flex justify-between text-sm">
              <span className="font-semibold">Sum arbeid</span>
              <span className="font-semibold">{fmtNOK(totals.arbeid)}</span>
            </div>
          </div>

          <div className="border rounded-lg p-3 md:p-4 mb-4">
            <h2 className="font-semibold mb-2 text-base md:text-lg">Deler (estimat)</h2>

            {partsLines.length ? (
              <ul className="divide-y">
                {partsLines.map((p, i) => {
                  const price = Number(p.price) || 0;
                  const qty = Math.max(1, Number(p.qty) || 1);
                  const subtotal = price * qty;

                  const discountKr = Math.max(0, Number(p.discount) || 0);
                  let discountPercent =
                    p.discountPercent != null ? Number(p.discountPercent) || 0 : 0;

                  if (!discountPercent && subtotal > 0 && discountKr > 0) {
                    discountPercent = Math.round((discountKr / subtotal) * 1000) / 10;
                  }

                  const line = Math.max(0, subtotal - discountKr);

                  const rabattText =
                    discountPercent > 0
                      ? `${discountPercent.toLocaleString("nb-NO", {
                        maximumFractionDigits: 1,
                      })} %`
                      : null;

                  return (
                    <li key={`${i}-${p.product || "part"}`} className="py-2 text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium break-words">
                            {p.product || "Del"}{" "}
                            {p.brand && <span className="text-gray-600">({p.brand})</span>}
                          </div>
                          {rabattText && (
                            <div className="text-xs text-gray-600">Rabatt: {rabattText}</div>
                          )}
                        </div>

                        <div className="hidden md:block text-right">
                          {p.isWarranty ? (
                            <>
                              <div className="font-medium text-gray-600 line-through whitespace-nowrap"><span className="sr-only">Ordinær pris </span>{fmtNOK(line)}</div>
                              <span className="inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Reklamasjon</span>
                            </>
                          ) : (
                            <div className="font-medium whitespace-nowrap">{fmtNOK(line)}</div>
                          )}
                        </div>
                      </div>

                      <div className="mt-1 flex items-baseline justify-between text-xs md:text-sm">
                        <div className="text-gray-700">
                          {qty} stk ·{" "}
                          {discountKr > 0 ? (
                            <>
                              <span className="line-through text-gray-600">
                                <span className="sr-only">Før rabatt </span>{fmtNOK(price)}
                              </span>{" "}
                              <span className="sr-only">Etter rabatt </span>
                              <span className="font-medium text-emerald-700">
                                {fmtNOK(qty > 0 ? line / qty : price)}
                              </span>
                            </>
                          ) : (
                            fmtNOK(price)
                          )}
                        </div>
                        <div className="md:hidden text-right">
                          {p.isWarranty ? (
                            <>
                              <div className="font-medium text-gray-600 line-through whitespace-nowrap"><span className="sr-only">Ordinær pris </span>{fmtNOK(line)}</div>
                              <span className="inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Reklamasjon</span>
                            </>
                          ) : (
                            <div className="font-medium whitespace-nowrap">{fmtNOK(line)}</div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="text-gray-600 text-sm">Ingen deler i estimatet.</div>
            )}

            <div className="mt-3 flex justify-between text-sm">
              <span className="font-semibold">Sum deler</span>
              <span className="font-semibold">{fmtNOK(totals.deler)}</span>
            </div>
          </div>

          {quoteComment && (
            <div className="border rounded-lg p-3 md:p-4 mb-4 bg-amber-50/70">
              <h2 className="font-semibold mb-2 text-base md:text-lg">
                Kommentar til pristilbud
              </h2>
              <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                {quoteComment}
              </p>
            </div>
          )}

          <div className="border rounded-lg p-3 md:p-4 mb-5 bg-gray-50">
            <div className="flex items-center justify-between text-base md:text-lg">
              <span className="font-semibold">Estimert total</span>
              <span className="font-bold">{fmtNOK(totals.total)}</span>
            </div>
          </div>

          {checklistSection}
        </>
      )}

      {debug && (
        <div className="fixed bottom-3 left-3 right-3 md:right-auto md:w-[420px] p-3 rounded border bg-white/95 shadow text-xs">
          <div className="font-semibold mb-1">DEBUG</div>
          <pre className="whitespace-pre-wrap break-words max-h-48 overflow-auto">
            {JSON.stringify(
              {
                status: svc?.status,
                quote: svc?.quote,
                checklistFromService: svc?.checklist,
                checklistSummaryFromService: svc?.checklistSummary,
                checklistFromSnapshot: svc?.customerSnapshot?.checklist,
                checklistSummaryMapped: checklistSummary,
                messagesLen: svc?.messages?.length || 0,
              },
              null,
              2
            )}
          </pre>
          <div className="mt-1 text-gray-500">
            Tips: skriv <code>localStorage.setItem("debugPortal", "1")</code> for å alltid
            vise denne.
          </div>
        </div>
      )}


      <p className="text-xs text-gray-500">
        Denne siden er en kundelenke. Del den ikke videre.
      </p>
    </div>
    </div>
  );
}
